import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { API_URL, authFetch } from "../utils/auth";
import OfferCard from "./OfferCard";
import PortalModal from "../common/PortalModal";
import "../../css/offers/OfferWorkspace.css";

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const AUTOSAVE_DEBOUNCE_MS = 800;
const DEFAULT_TERMS = "This offer is based on the plan, features, and scope notes listed here. Work begins after the required agreement and payment conditions are completed. Additional work outside the accepted scope may require a separate approved change. Delivery timing may adjust if scope, approvals, content, or other client dependencies change. Final deliverables remain governed by the applicable South Central Development service agreement. SCD may manage normal technical infrastructure for the project and bill usage that exceeds included allowances at SCD's then-current service rates.";

function formatMoney(value) {
  const amount = Number(value || 0);
  return money.format(amount).replace(/\.00$/, "");
}

function formatDays(value) {
  const days = Number(value || 0);
  if (!days) return "0 days";
  const formatted = Number.isInteger(days) ? String(days) : String(days).replace(/0+$/, "").replace(/\.$/, "");
  return `${formatted} ${days === 1 ? "day" : "days"}`;
}

function planOptionLabel(plan) {
  const price = String(plan.display_price || formatMoney(plan.base_price)).replace(/^Starting at\s+/i, "");
  return `${plan.name} — Starting at ${price}`;
}

export default function OfferWorkspace({ conversationId, onOfferSent }) {
  const [workspace, setWorkspace] = useState(null);
  const [planId, setPlanId] = useState("");
  const [featureIds, setFeatureIds] = useState([]);
  const [notes, setNotes] = useState("");
  const [terms, setTerms] = useState(DEFAULT_TERMS);
  const [implementationNotes, setImplementationNotes] = useState({});
  const [expandedNoteIds, setExpandedNoteIds] = useState([]);
  const [preview, setPreview] = useState(null);
  const [draft, setDraft] = useState(null);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [compareOfferId, setCompareOfferId] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [featureSearch, setFeatureSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const autosaveTimerRef = useRef(null);
  const pendingSaveRef = useRef(null);
  const latestDraftRef = useRef(null);

  const canBuildOffers = localStorage.getItem("is_partner") === "true" || localStorage.getItem("is_superuser") === "true" || localStorage.getItem("is_internal") === "true";
  const project = workspace?.project;
  const plans = useMemo(() => workspace?.plans || [], [workspace]);
  const selectedPlan = useMemo(() => plans.find((plan) => String(plan.id) === String(planId)), [plans, planId]);
  const allFeatures = useMemo(() => selectedPlan?.features || [], [selectedPlan]);
  const includedFeatureIds = useMemo(() => new Set((selectedPlan?.included_feature_ids || []).map(String)), [selectedPlan]);
  const selectedFeatureIds = useMemo(() => new Set(featureIds.map(String)), [featureIds]);
  const includedFeatures = useMemo(() => allFeatures.filter((feature) => includedFeatureIds.has(String(feature.id)) || feature.included), [allFeatures, includedFeatureIds]);
  const addedFeatures = useMemo(() => allFeatures.filter((feature) => selectedFeatureIds.has(String(feature.id)) && !includedFeatureIds.has(String(feature.id)) && !feature.included), [allFeatures, selectedFeatureIds, includedFeatureIds]);
  const pickerCategories = useMemo(() => {
    const categories = Array.from(new Set(allFeatures.map((feature) => feature.category).filter(Boolean)));
    return categories.sort((a, b) => String(a).localeCompare(String(b)));
  }, [allFeatures]);
  const pickerFeatures = useMemo(() => {
    const search = featureSearch.trim().toLowerCase();
    return allFeatures.filter((feature) => {
      if (categoryFilter !== "all" && feature.category !== categoryFilter) return false;
      if (!search) return true;
      return [feature.name, feature.description, feature.category]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search));
    });
  }, [allFeatures, categoryFilter, featureSearch]);
  const revisionableStatuses = new Set(["sent", "viewed", "declined", "changes_requested", "expired", "accepted"]);
  const compareOffer = useMemo(() => (workspace?.offers || []).find((offer) => String(offer.id) === String(compareOfferId)), [workspace, compareOfferId]);
  const compareBaseOffer = useMemo(() => (workspace?.offers || []).find((offer) => offer.id === compareOffer?.supersedes_id), [workspace, compareOffer]);
  const acceptedOffer = useMemo(() => (workspace?.offers || []).find((offer) => ["accepted", "payment_pending", "paid"].includes(offer.status) && offer.is_current) || null, [workspace]);
  const readOnly = Boolean(acceptedOffer);

  useEffect(() => {
    latestDraftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    if (!conversationId) return;
    authFetch(`${API_URL}/conversations/${conversationId}/sales-workspace/`)
      .then((res) => (res.ok ? res.json() : { project: null }))
      .then((data) => {
        setWorkspace(data);
        const initialPlan = data.suggested_plan_id || data.plans?.[0]?.id || "";
        setPlanId(String(initialPlan));
        const latestDraft = (data.offers || []).find((offer) => offer.status === "draft" && offer.can_edit);
        if (latestDraft) {
          setDraft(latestDraft);
          setPlanId(String(latestDraft.base_plan?.id || initialPlan));
          setFeatureIds((latestDraft.items || []).filter((item) => item.feature_id && !item.is_included).map((item) => String(item.feature_id)));
          setNotes(latestDraft.client_facing_notes || "");
          setTerms(latestDraft.terms || DEFAULT_TERMS);
          const notesByFeature = {};
          const expandedNotes = [];
          (latestDraft.items || []).forEach((item) => {
            if (item.feature_id && item.staff_internal_note) {
              notesByFeature[String(item.feature_id)] = item.staff_internal_note;
              expandedNotes.push(String(item.feature_id));
            }
          });
          setImplementationNotes(notesByFeature);
          setExpandedNoteIds(expandedNotes);
        }
      });
  }, [conversationId]);

  useEffect(() => {
    if (!project || !planId || readOnly) return;
    authFetch(`${API_URL}/offers/preview/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ project_id: project.id, plan_id: Number(planId), feature_ids: featureIds.map(Number) }),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setPreview(data));
  }, [project, planId, featureIds, readOnly]);

  function offerStatusLabel(offer) {
    if (!offer) return "None";
    if (offer.accepted_at || ["accepted", "payment_pending", "paid"].includes(offer.status)) return "Accepted";
    return offer.status_label || offer.status || "Draft";
  }

  function paymentTermsLabel(offer) {
    if (!offer) return "Automatic payment schedule";
    if (offer.payment_structure === "deposit_balance") return "Deposit / final payment";
    return offer.payment_structure_label || "Full payment";
  }

  function setImplementationNote(featureId, value) {
    setImplementationNotes((current) => ({ ...current, [String(featureId)]: value }));
  }

  function isUnavailableFeature(feature) {
    const availability = String(feature.availability || feature.availability_status || "").toLowerCase();
    return feature.available === false || feature.is_available === false || availability === "unavailable" || availability === "not_available";
  }


  function addFeature(featureId) {
    const key = String(featureId);
    if (includedFeatureIds.has(key)) return;
    setFeatureIds((current) => current.includes(key) ? current : [...current, key]);
  }

  function removeFeature(featureId) {
    const key = String(featureId);
    if (includedFeatureIds.has(key)) return;
    setFeatureIds((current) => current.filter((item) => item !== key));
  }

  function toggleNote(featureId) {
    const key = String(featureId);
    setExpandedNoteIds((current) => current.includes(key) ? current.filter((item) => item !== key) : [...current, key]);
  }

  function featurePrice(feature, included = false) {
    if (included) return "$0";
    if (feature.requires_quote) return "Custom quote";
    return formatMoney(feature.addon_price);
  }

  function renderEditableFeatureRow(feature, variant) {
    const id = String(feature.id);
    const included = variant === "included";
    const added = variant === "added";
    const noteOpen = expandedNoteIds.includes(id);

    return (
      <article className={`builder-feature-row ${variant}`} key={id}>
        <div className="builder-feature-copy">
          <strong>{feature.name}</strong>
          {feature.description && <p>{feature.description}</p>}
          {noteOpen && (
            <label className="implementation-note">Internal implementation note
              <textarea value={implementationNotes[id] || ""} onChange={(e) => setImplementationNote(id, e.target.value)} placeholder="Client does not currently want this feature enabled at launch." />
            </label>
          )}
        </div>
        <div className="builder-feature-meta">
          <span className={included ? "feature-status included" : "feature-status addon"}>{included ? "Included" : "Add-on"}</span>
          <strong>{featurePrice(feature, included)}</strong>
          {!included && <small>+{formatDays(feature.extra_days)}</small>}
          {included && <small>Included with {selectedPlan?.name || "selected package"}</small>}
        </div>
        <div className="builder-feature-actions">
          {included && <button type="button" className="link-button" onClick={() => toggleNote(id)}>{noteOpen ? "Hide note" : "Add internal note"}</button>}
          {added && <button type="button" className="link-button danger" onClick={() => removeFeature(id)}>Remove</button>}
        </div>
      </article>
    );
  }

  function renderPickerFeature(feature) {
    const id = String(feature.id);
    const included = includedFeatureIds.has(id) || feature.included;
    const added = selectedFeatureIds.has(id);
    const unavailable = !included && isUnavailableFeature(feature);
    const disabled = included || added || unavailable;
    const statusLabel = included ? "Included with selected package" : added ? "Already added" : unavailable ? (feature.availability_label || "Unavailable for this package") : "Available add-on";
    const actionLabel = included ? "Already included" : added ? "Added" : unavailable ? "Unavailable" : "Add";

    return (
      <article className={`picker-feature-row ${disabled ? "disabled" : ""}`} key={id}>
        <div className="builder-feature-copy">
          <div className="picker-feature-title">
            <strong>{feature.name}</strong>
            {feature.category && <span className="feature-category-pill">{feature.category}</span>}
          </div>
          {feature.description && <p>{feature.description}</p>}
        </div>
        <div className="builder-feature-meta">
          <span className={included ? "feature-status included" : unavailable ? "feature-status unavailable" : "feature-status addon"}>{statusLabel}</span>
          <strong>{featurePrice(feature, included)}</strong>
          {!included && !feature.requires_quote && <small>+{formatDays(feature.extra_days)}</small>}
        </div>
        <button type="button" className="picker-add-button" disabled={disabled} onClick={() => addFeature(id)}>{actionLabel}</button>
      </article>
    );
  }

  const saveDraft = useCallback(async (silent = false) => {
    if (!project || !planId || readOnly) return latestDraftRef.current;
    const currentDraft = latestDraftRef.current;
    setStatus(silent ? "Autosaving..." : "Saving...");
    const url = currentDraft ? `${API_URL}/offers/${currentDraft.id}/` : `${API_URL}/conversations/${conversationId}/offers/`;
    const method = currentDraft ? "PATCH" : "POST";
    const response = await authFetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ project_id: project.id, plan_id: Number(planId), feature_ids: featureIds.map(Number), implementation_notes: implementationNotes, client_facing_notes: notes, terms }),
    });
    const data = await response.json();
    if (!response.ok) {
      setStatus(data.detail || "Save failed - retrying");
      return null;
    }
    latestDraftRef.current = data;
    setDraft(data);
    setStatus(silent ? "Autosaved" : "Saved");
    return data;
  }, [conversationId, featureIds, implementationNotes, notes, planId, project, readOnly, terms]);

  const flushPendingSave = useCallback(async () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    if (pendingSaveRef.current) {
      await pendingSaveRef.current;
    }
    return saveDraft(true);
  }, [saveDraft]);

  useEffect(() => {
    if (!project || !planId || readOnly) return undefined;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      pendingSaveRef.current = saveDraft(true).finally(() => {
        pendingSaveRef.current = null;
      });
      autosaveTimerRef.current = null;
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [featureIds, implementationNotes, notes, planId, project, readOnly, saveDraft, terms]);

  async function submitForReview() {
    const saved = await flushPendingSave();
    if (!saved) return;
    setStatus("Submitting for review...");
    const response = await authFetch(`${API_URL}/offers/${saved.id}/submit-review/`, { method: "POST" });
    const data = await response.json();
    if (!response.ok) setStatus(data.detail || "Unable to submit for review.");
    else { setDraft(data); setStatus("Submitted for SCD review."); onOfferSent?.(); }
  }

  async function createRevision(offer) {
    const response = await authFetch(`${API_URL}/offers/${offer.id}/revision/`, { method: "POST" });
    const data = await response.json();
    if (!response.ok) { setStatus(data.detail || "Unable to create revision."); return; }
    setDraft(data);
    setPlanId(String(data.base_plan?.id || planId));
    setFeatureIds((data.items || []).filter((item) => item.feature_id && !item.is_included).map((item) => String(item.feature_id)));
    setNotes(data.client_facing_notes || "");
    setTerms(data.terms || DEFAULT_TERMS);
    setStatus("Revision draft created.");
  }

  async function sendDraft() {
    if (busy) return;
    setBusy(true);
    try {
      const saved = await flushPendingSave();
      if (!saved) return;
      setStatus("Sending offer...");
      const response = await authFetch(`${API_URL}/offers/${saved.id}/send/`, { method: "POST" });
      const data = await response.json();
      if (!response.ok) {
        setStatus(data.detail || "Unable to send offer.");
        return;
      }
      setDraft(null);
      setStatus("Offer sent.");
      onOfferSent?.();
    } finally {
      setBusy(false);
    }
  }

  if (!workspace || !project) return null;

  const historyOffers = workspace.offers || [];
  const currentOffer = acceptedOffer || historyOffers.find((offer) => offer.is_current) || historyOffers[0] || null;
  const selectedItems = preview?.items || [];
  const readOnlyItems = acceptedOffer?.items || [];

  return (
    <aside className="offer-workspace-panel">
      <div className="offer-panel-section">
        <span className="offer-eyebrow">Sales workspace</span>
        <h2>{project.business_name || project.title}</h2>
        <p>{project.project_goal || project.description}</p>
        <dl>
          <div><dt>Client</dt><dd>{project.client_name || "Not provided"}</dd></div>
          <div><dt>Status</dt><dd>{project.delivery_status_label || project.status_label}</dd></div>
          <div><dt>Offer Status</dt><dd>{offerStatusLabel(currentOffer)}</dd></div>
          <div><dt>Budget</dt><dd>{project.budget}</dd></div>
          <div><dt>Timeline</dt><dd>{project.timeline}</dd></div>
        </dl>
      </div>

      {canBuildOffers && readOnly && acceptedOffer && <div className="offer-panel-section offer-readonly">
        <span className="offer-eyebrow">Offer Accepted</span>
        <h3>This offer was accepted by the client and can no longer be edited.</h3>
        <dl>
          <div><dt>Plan</dt><dd>{acceptedOffer.base_plan?.name || "Accepted plan"}</dd></div>
          <div><dt>Accepted</dt><dd>{acceptedOffer.accepted_at ? new Date(acceptedOffer.accepted_at).toLocaleDateString() : "Accepted"}</dd></div>
          <div><dt>Total</dt><dd>{formatMoney(acceptedOffer.total_amount)}</dd></div>
          <div><dt>Payment terms</dt><dd>{paymentTermsLabel(acceptedOffer)}</dd></div>
        </dl>
        <div className="readonly-offer-lines">
          {readOnlyItems.map((item) => (
            <article key={item.id}>
              <strong>{item.name}</strong>
              <span>{item.is_included ? "Included with plan" : `Add-on: ${formatMoney(item.line_total)}`}</span>
              {item.description && <p>{item.description}</p>}
              {item.staff_internal_note && <em>Implementation note: {item.staff_internal_note}</em>}
            </article>
          ))}
        </div>
        {acceptedOffer.client_facing_notes && <div className="offer-notes"><strong>Client-facing scope notes</strong><p>{acceptedOffer.client_facing_notes}</p></div>}
        {acceptedOffer.terms && <div className="offer-notes"><strong>Terms</strong><p>{acceptedOffer.terms}</p></div>}
        <p className="offer-warning"><strong>Internal guidance</strong><span>For changes to an existing signed project, use a Change Order. For a separate engagement, start a new project.</span></p>
        <Link className="offer-start-project" to="/new-project">Start New Project</Link>
      </div>}

      {canBuildOffers && !readOnly && <div className="offer-panel-section">
        <h3>Offer Builder</h3>
        <label>Base plan
          <select value={planId} onChange={(e) => { setPlanId(e.target.value); setFeatureIds([]); setImplementationNotes({}); setExpandedNoteIds([]); setPickerOpen(false); }}>
            {plans.map((plan) => <option value={plan.id} key={plan.id}>{planOptionLabel(plan)}</option>)}
          </select>
        </label>

        <div className="builder-feature-section">
          <div className="builder-feature-heading"><h4>Included Features</h4><span>{includedFeatures.length}</span></div>
          {includedFeatures.length ? includedFeatures.map((feature) => renderEditableFeatureRow(feature, "included")) : <p className="offer-feature-empty">No included features for this package.</p>}
        </div>

        <div className="builder-feature-section">
          <div className="builder-feature-heading"><h4>Added Features</h4><span>{addedFeatures.length}</span></div>
          {addedFeatures.length ? addedFeatures.map((feature) => renderEditableFeatureRow(feature, "added")) : <p className="offer-feature-empty">No add-ons selected yet.</p>}
        </div>

        <button type="button" className="add-feature-button" onClick={() => setPickerOpen(true)}>
          Add Feature
        </button>

        <label>Client-facing scope notes
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Summarize what the client sees in this offer." />
        </label>
        <label>Internal implementation notes
          <textarea value={Object.values(implementationNotes).filter(Boolean).join("\n\n")} readOnly placeholder="Use feature-specific note fields above for dev-team implementation notes." />
        </label>
        <label>Terms
          <textarea value={terms} onChange={(e) => setTerms(e.target.value)} />
        </label>
      </div>}

      {canBuildOffers && !readOnly && (
        <PortalModal open={pickerOpen} title="Add Feature" primaryLabel="Close" variant="offer-feature" onClose={() => setPickerOpen(false)}>
          <p className="feature-picker-subtitle">Choose additional features for this offer.</p>
          <div className="feature-picker-toolbar">
            <label>Search
              <input type="text" value={featureSearch} onChange={(event) => setFeatureSearch(event.target.value)} placeholder="Search by feature, description, or category" />
            </label>
            {pickerCategories.length > 1 && <label>Category
              <select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
                <option value="all">All categories</option>
                {pickerCategories.map((category) => <option value={category} key={category}>{category}</option>)}
              </select>
            </label>}
          </div>
          <div className="feature-picker-list">
            {pickerFeatures.length ? pickerFeatures.map(renderPickerFeature) : <p className="offer-feature-empty">No features match this search.</p>}
          </div>
        </PortalModal>
      )}

      {canBuildOffers && !readOnly && preview && (
        <div className="offer-panel-section price-summary">
          <h3>Price Summary</h3>
          <div className="summary-row"><span>Base plan</span><strong>{selectedPlan?.name || "Plan"}</strong></div>
          <div className="summary-row"><span>Base price</span><strong>{selectedPlan ? selectedPlan.display_price : formatMoney(0)}</strong></div>
          <div className="summary-row"><span>Add-ons</span><strong>{formatMoney(selectedItems.filter((item) => !item.is_included && item.pricing_source !== "plan").reduce((sum, item) => sum + Number(item.line_total || 0), 0))}</strong></div>
          <div className="summary-row"><span>Subtotal</span><strong>{formatMoney(preview.subtotal)}</strong></div>
          <div className="summary-row"><span>Total</span><strong>{formatMoney(preview.total)}</strong></div>
          <div className="summary-row"><span>Added time</span><strong>{formatDays(preview.extra_days)}</strong></div>
          {preview.requires_staff_review && <p className="offer-warning">Custom quote items need SCD staff pricing before this offer can be sent.</p>}
          <div className="payment-schedule">
            <h4>Payment Schedule</h4>
            <p className="automatic-payment-note">Calculated automatically from the final offer total.</p>
            {Number(preview.total || 0) > 500 ? <>
              <div className="payment-card-grid">
                <article className="payment-card">
                  <span>Due Now</span>
                  <strong>{formatMoney(preview.payment_schedule?.amount_due_now || 0)}</strong>
                  <small>50% deposit</small>
                </article>
                <article className="payment-card">
                  <span>Final Balance</span>
                  <strong>{formatMoney(preview.payment_schedule?.remaining_balance || 0)}</strong>
                  <small>50% before launch</small>
                </article>
              </div>
              <p className="payment-due-note">Final balance is due before launch.</p>
            </> : <>
              <div className="payment-card-grid single">
                <article className="payment-card">
                  <span>Due Now</span>
                  <strong>{formatMoney(preview.payment_schedule?.amount_due_now || preview.total)}</strong>
                  <small>100% payment</small>
                </article>
              </div>
              <p className="payment-due-note">No remaining balance.</p>
            </>}
          </div>
          {preview.requires_staff_review || draft?.status === "changes_requested" ? <button type="button" className="primary" onClick={submitForReview} disabled={busy}>Submit for SCD Review</button> : <button type="button" className="primary" onClick={sendDraft} disabled={busy}>Send Offer</button>}
          {status && <small className="autosave-status">{status}</small>}
        </div>
      )}

      {historyOffers.length > 0 && (
        <div className="offer-panel-section">
          <h3>Offer History</h3>
          <label>Compare revision
            <select value={compareOfferId} onChange={(event) => setCompareOfferId(event.target.value)}>
              <option value="">Select an offer</option>
              {historyOffers.filter((offer) => offer.supersedes_id).map((offer) => (
                <option value={offer.id} key={offer.id}>Revision {offer.revision_number} - {offer.status_label}</option>
              ))}
            </select>
          </label>
          {compareOffer && compareBaseOffer && (
            <div className="offer-comparison">
              <div><span>Previous total</span><strong>{formatMoney(compareBaseOffer.total_amount)}</strong></div>
              <div><span>Revision total</span><strong>{formatMoney(compareOffer.total_amount)}</strong></div>
              <div><span>Previous days</span><strong>{compareBaseOffer.estimated_extra_days}</strong></div>
              <div><span>Revision days</span><strong>{compareOffer.estimated_extra_days}</strong></div>
            </div>
          )}
          {historyOffers.slice(0, 4).map((offer) => (
            <div key={offer.id} className="offer-history-item">
              <OfferCard offer={offer} showClientOfferAction={false} />
              {revisionableStatuses.has(offer.status) && offer.is_current && !["accepted", "payment_pending", "paid"].includes(offer.status) && (
                <button type="button" onClick={() => createRevision(offer)}>Create Revision</button>
              )}
            </div>
          ))}
        </div>
      )}
    </aside>
  );
}
